import moment from 'moment'

export class ModalManager {
  constructor (controller) {
    this.controller = controller

    // Modal visibility state
    this.showEditModal = false
    this.showOverlapConflictModal = false
    this.showCreateModal = false
    this.showCalendarModal = false

    // Pending state for reverts
    this.pendingChangeInfo = null

    // Conflict state
    this.conflictingEvents = []
    this.pendingEvent = null
  }

  /**
       * Open the main calendar modal
       */
  openCalendarModal () {
    this.showCalendarModal = true

    // Remove any existing escape handler before adding a new one
    // (guards against double-open without intervening close)
    if (this._escapeHandler) {
      document.removeEventListener('keydown', this._escapeHandler)
    }

    // Listen for Escape key to close modal
    this._escapeHandler = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        // Close topmost modal first
        if (this.showEditModal) {
          this.closeEventEditModal()
        } else if (this.showCreateModal) {
          this.closeCreateIntervalModal()
        } else if (this.showOverlapConflictModal) {
          this.closeOverlapModal(true)
        } else {
          this.controller.closeCalendarModal()
        }
        this.controller._$scope.$applyAsync()
      }
    }
    document.addEventListener('keydown', this._escapeHandler)

    // Reinitialize calendar when modal opens to ensure fresh state
    if (this.controller.calendarManager) {
      // Use $timeout to wait for modal DOM to be ready
      this.controller._$timeout(() => {
        this.controller.calendarManager.waitForVisibilityAndInit()
      }, 0)
    }
  }

  /**
       * Close the main calendar modal
       */
  closeCalendarModal () {
    // Remove Escape key listener
    if (this._escapeHandler) {
      document.removeEventListener('keydown', this._escapeHandler)
      this._escapeHandler = null
    }

    // Close any nested modals first
    this.closeEventEditModal()
    this.closeOverlapModal(true)
    this.closeCreateIntervalModal()

    // Destroy calendar to free resources and prevent stale state
    if (this.controller.calendarManager) {
      this.controller.calendarManager.destroy()
    }

    // Close main modal
    this.showCalendarModal = false

    // Notify parent component
    if (this.controller.onClose) {
      this.controller.onClose()
    }
  }

  /**
       * Open event edit modal with event data
       * @param {Object} calendarEvent - FullCalendar event object
       */
  openEventEditModal (calendarEvent) {
    const extendedProps = calendarEvent.extendedProps

    if (!extendedProps || !extendedProps.intervalId) {
      this.controller._$log.warn('[ScheduleCalendar] No interval data found for event')
      return
    }

    const startMoment = moment(calendarEvent.start)
    const endMoment = moment(calendarEvent.end)

    this.controller._$log.info('[ScheduleCalendar] Opening edit modal for event:', {
      intervalId: extendedProps.intervalId,
      start: calendarEvent.start,
      end: calendarEvent.end,
      startFormatted: startMoment.format('YYYY-MM-DD HH:mm:ss'),
      endFormatted: endMoment.format('YYYY-MM-DD HH:mm:ss')
    })

    // Set data on StateManager
    if (this.controller.stateManager) {
      this.controller.stateManager.editingEvent = {
        intervalId: extendedProps.intervalId,
        sceneCollectionId: extendedProps.sceneCollectionId,
        title: extendedProps.sceneCollection ? extendedProps.sceneCollection.name : calendarEvent.title,
        startDate: startMoment.toDate(),
        startTime: startMoment.toDate(),
        endDate: endMoment.toDate(),
        endTime: endMoment.toDate(),
        priority: extendedProps.priority || 1,
        originalEvent: calendarEvent
      }

      this.controller._$log.info('[ScheduleCalendar] Set editingEvent on StateManager')
    }

    // Use $timeout to avoid digest conflicts
    this.controller._$timeout(() => {
      this.showEventEditModal()
    }, 0)
  }

  /**
       * Show the event edit modal
       */
  showEventEditModal () {
    this.showEditModal = true
  }

  /**
       * Close the event edit modal
       */
  closeEventEditModal () {
    this.showEditModal = false
    if (this.controller.stateManager) {
      this.controller.stateManager.editingEvent = null
    }
  }

  /**
       * Show the overlap conflict modal
       */
  showOverlapModal () {
    this.showOverlapConflictModal = true
  }

  /**
       * Close the overlap conflict modal
       * @param {boolean} revert - Whether to revert the pending change
       */
  closeOverlapModal (revert = false) {
    this.showOverlapConflictModal = false

    // Revert pending change if requested
    if (revert && this.pendingChangeInfo && this.pendingChangeInfo.revert) {
      this.controller._$log.info('[ScheduleCalendar] Reverting change due to modal close')
      this.pendingChangeInfo.revert()
      this.pendingChangeInfo = null
    }

    // Clear conflict state
    this.conflictingEvents = []
    this.pendingEvent = null
  }

  /**
       * Open create interval modal for selected time range
       * @param {Date} startTime - Selected start time
       * @param {Date} endTime - Selected end time
       */
  openCreateIntervalModal (startTime, endTime) {
    this.controller._$log.info('[ScheduleCalendar] Opening create interval modal', { startTime, endTime })

    // Load available scene collections for the current scene via StateManager
    if (this.controller.stateManager) {
      this.controller.stateManager.loadAvailableSceneCollections().then(() => {
        // Set up create interval data on StateManager
        this.controller.stateManager.creatingInterval = {
          startTime: moment(startTime),
          endTime: moment(endTime),
          duration: moment(endTime).diff(moment(startTime), 'minutes'),
          selectedSceneCollectionId: null,
          priority: 100
        }

        // Show the modal
        this.controller._$timeout(() => {
          this.showCreateModal = true
        })

        this.controller._$log.info('[ScheduleCalendar] Create interval modal data ready')
      }).catch(error => {
        this.controller._$log.error('[ScheduleCalendar] Failed to load scene collections:', error)
        window.alert('Failed to load available content. Please check your connection and try again.')
      })
    } else {
      this.controller._$log.error('[ScheduleCalendar] StateManager not available')
    }
  }

  /**
       * Close create interval modal
       */
  closeCreateIntervalModal () {
    this.showCreateModal = false
    if (this.controller.stateManager) {
      this.controller.stateManager.creatingInterval = null
    }
  }
}
